import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../styles/content-hub.css';
import useRevealAnimation from '../../hooks/useRevealAnimation';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { formatContentDate } from './mockContent';
import { ApiRequestError, fetchContentDetail } from './contentHubApi';
import type { ContentEntry, ContentKind, SupportedLocale } from './types';

type ContentHubDetailPageProps = {
  kind: ContentKind;
};

const normalizeLanguage = (language?: string | null): SupportedLocale => {
  const normalized = (language ?? '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('ja')) return 'ja';
  return 'en';
};

const ContentHubDetailPage: React.FC<ContentHubDetailPageProps> = ({ kind }) => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const [item, setItem] = useState<ContentEntry | null | undefined>(undefined);
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const locale = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  useDocumentTitle(item?.title || t(kind === 'news' ? 'ContentHub.news.hero.title' : 'ContentHub.resources.hero.title'));

  useRevealAnimation(pageRef);

  useEffect(() => {
    let cancelled = false;

    if (!slug) {
      setItem(null);
      setBackendUnavailable(false);
      return undefined;
    }

    setItem(undefined);
    setBackendUnavailable(false);

    const load = async () => {
      try {
        const result = await fetchContentDetail(kind, slug, locale);
        if (cancelled) return;
        setItem(result.item);

        const translatedSlug = result.translationSlugs[locale];
        if (translatedSlug && translatedSlug !== slug) {
          navigate(kind === 'news' ? `/resources/news/${translatedSlug}` : `/resources/library/${translatedSlug}`, { replace: true });
        }
      } catch (error) {
        if (cancelled) return;

        if (error instanceof ApiRequestError && error.kind === 'network') {
          setBackendUnavailable(true);
          setItem(null);
          return;
        }

        if (error instanceof ApiRequestError && error.status === 404) {
          setItem(null);
          return;
        }

        setBackendUnavailable(true);
        setItem(null);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [kind, locale, navigate, slug]);

  if (item === undefined) {
    return (
      <div className="content-hub-page" ref={pageRef}>
        <section className="section-container content-hub-page-shell">
          <div className="content-hub-empty">{t('ContentHub.common.loading')}</div>
        </section>
      </div>
    );
  }

  if (backendUnavailable) {
    return (
      <div className="content-hub-page" ref={pageRef}>
        <section className="section-container content-hub-page-shell">
          <div className="content-hub-empty">{t('ContentHub.common.backendUnavailable')}</div>
        </section>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="content-hub-page" ref={pageRef}>
        <section className="section-container content-hub-page-shell">
          <div className="content-hub-not-found">
            <h1>{t('ContentHub.common.notFoundTitle')}</h1>
            <p>{t('ContentHub.common.notFoundDescription')}</p>
            <Link to={kind === 'news' ? '/resources/news' : '/resources'} className="content-hub-back-link">
              {kind === 'news' ? t('ContentHub.common.backToNews') : t('ContentHub.common.backToResources')}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="content-hub-page" ref={pageRef}>
      <section className="section-container content-hub-page-shell">
        <article className="content-hub-detail-card">
          <div className="content-hub-detail-shell">
            <Link to={kind === 'news' ? '/resources/news' : '/resources'} className="content-hub-back-link">
              {kind === 'news' ? t('ContentHub.common.backToNews') : t('ContentHub.common.backToResources')}
            </Link>

            <div className="content-hub-detail-meta">
              <span className="content-hub-card-badge">{item.badge}</span>
              <span>{t('ContentHub.common.publishedOn')}: {formatContentDate(item.date, i18n.resolvedLanguage)}</span>
            </div>

            <h1 className="content-hub-detail-title">{item.title}</h1>
            <p className="content-hub-detail-summary">{item.summary}</p>

            {item.coverImageUrl ? (
              <div className="content-hub-detail-visual has-image" aria-hidden="true">
                <img src={item.coverImageUrl} alt="" className="content-hub-cover-image" loading="lazy" />
              </div>
            ) : (
              <div className={`content-hub-detail-visual tone-${item.coverTone}`} aria-hidden="true">
                <div className="content-hub-card-orb" />
                <div className="content-hub-card-lines">
                  <span />
                  <span />
                  <span className="is-short" />
                </div>
              </div>
            )}
          </div>
          <div className="content-hub-detail-body">
            {item.body.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
};

export default ContentHubDetailPage;
